import { AnimatePresence, motion } from "framer-motion";
import { Check, Clock, Lock, PartyPopper } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { BUILD_PACKAGES, BUILD_ADDONS, calcBuild } from "@/components/BuildConfig";

export default function BuildYourBar({ build, setBuild }) {
  const { buildPkg, hours, clusters, addonsTotal, hoursTotal, guestsTotal, buildTotal } = calcBuild(build);

  const togglePkg = (id) => setBuild((b) => ({ ...b, pkg: b.pkg === id ? "" : id }));
  const toggleAddon = (id) =>
    setBuild((b) => ({
      ...b,
      addons: b.addons.includes(id) ? b.addons.filter((a) => a !== id) : [...b.addons, id],
    }));
  const setHours = (h) => setBuild((b) => ({ ...b, hours: Math.min(6, Math.max(0, h)) }));
  const setGuestClusters = (c) => setBuild((b) => ({ ...b, extraGuests: Math.min(8, Math.max(0, c)) }));

  const secondThreshold = !build.pkg ? null : build.pkg === "full_pour" ? 100 : 75;
  const pkgChosen = !!buildPkg;

  return (
    <section id="build" data-testid="build-own-section" className="bg-blush px-5 py-24 sm:px-8 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <Reveal>
              <span className="eyebrow text-espresso/50">Build your own</span>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl">
                Build your own <em className="italic text-rose">package</em>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="max-w-sm text-sm leading-relaxed text-espresso/65 sm:text-base">
              Pick a package, then tap the extras you want — your total updates as you go.
              Happy with it? Book your date below.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-14 overflow-hidden rounded-[2rem] bg-espresso-deep shadow-[0_40px_80px_-40px_rgba(29,19,12,0.6)]">
            <div className="grain p-8 sm:p-12 lg:p-14">
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-cream/50">
                1 — Pick your package
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {BUILD_PACKAGES.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`build-own-pkg-${p.id}`}
                    onClick={() => togglePkg(p.id)}
                    aria-pressed={build.pkg === p.id}
                    className={`relative flex w-full items-center justify-between gap-3 rounded-2xl border p-5 text-left transition-all duration-300 ${
                      build.pkg === p.id
                        ? "border-gold bg-white/[0.06]"
                        : "border-cream/15 bg-white/[0.02] hover:border-cream/35"
                    }`}
                  >
                    <span>
                      <span className="flex flex-wrap items-center gap-2 font-display text-xl text-cream sm:text-2xl">
                        {p.name}
                        {p.popular && (
                          <span className="rounded-full bg-gold px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-espresso">
                            Popular
                          </span>
                        )}
                      </span>
                      <span className="mt-1 block text-[11px] leading-relaxed text-cream/55">{p.note}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <span className="font-display text-2xl text-gold">${p.price.toLocaleString()}</span>
                      <span
                        className={`flex h-5 w-5 items-center justify-center rounded-full border transition-colors ${
                          build.pkg === p.id ? "border-gold bg-gold text-espresso" : "border-cream/30 text-transparent"
                        }`}
                      >
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                    </span>
                  </button>
                ))}
              </div>

              <AnimatePresence initial={false}>
                {pkgChosen ? (
                  <motion.div
                    key="extras"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                    data-testid="build-own-extras"
                  >
                    <p className="mt-8 text-[11px] font-bold uppercase tracking-[0.22em] text-cream/50">
                      2 — Now tap the extras you want
                    </p>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      {BUILD_ADDONS.map((a) => {
                        const on = build.addons.includes(a.id);
                        return (
                          <button
                            key={a.id}
                            data-testid={`build-own-addon-${a.id}`}
                            onClick={() => toggleAddon(a.id)}
                            aria-pressed={on}
                            className={`relative flex h-full w-full flex-col rounded-2xl border p-4 text-left transition-all duration-300 ${
                              on ? "border-gold bg-white/[0.06]" : "border-cream/15 bg-white/[0.02] hover:border-cream/35"
                            }`}
                          >
                            {on && (
                              <span className="absolute top-3 right-3 flex h-5 w-5 items-center justify-center rounded-full bg-gold text-espresso">
                                <Check className="h-3 w-3" strokeWidth={3} />
                              </span>
                            )}
                            <span
                              className={`flex h-9 w-9 items-center justify-center rounded-xl transition-colors duration-300 ${
                                on ? "bg-gold text-espresso" : "bg-cream/10 text-gold"
                              }`}
                            >
                              <a.icon className="h-4 w-4" />
                            </span>
                            <span className="mt-3 font-display text-xl leading-tight text-cream">{a.name}</span>
                            <span className="mt-1 flex-1 text-xs leading-relaxed text-cream/55">{a.copy}</span>
                            {on && (
                              <span className="mt-2 text-sm font-semibold text-gold" data-testid={`build-own-addon-price-${a.id}`}>
                                +${a.price}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    <p className="mt-8 text-[11px] font-bold uppercase tracking-[0.22em] text-cream/50">
                      3 — Guests & extra time
                    </p>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <div
                        data-testid="build-own-guests-row"
                        className={`flex flex-col items-start justify-between gap-3 rounded-2xl border p-4 transition-all duration-300 ${
                          clusters > 0 ? "border-gold bg-white/[0.06]" : "border-cream/15 bg-white/[0.02]"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors duration-300 ${
                              clusters > 0 ? "bg-gold text-espresso" : "bg-cream/10 text-gold"
                            }`}
                          >
                            <PartyPopper className="h-4 w-4" />
                          </span>
                          <p className="text-sm text-cream/70">Additional guests — up to 200 extra</p>
                        </div>
                        <div className="flex items-center gap-3">
                          {clusters > 0 && (
                            <span className="font-display text-xl text-gold" data-testid="build-own-guests-price">
                              +${guestsTotal.toLocaleString()}
                            </span>
                          )}
                          <button
                            data-testid="build-own-guests-minus"
                            onClick={() => setGuestClusters(clusters - 1)}
                            disabled={clusters === 0}
                            aria-label="Remove 25 guests"
                            className="flex h-9 w-9 items-center justify-center rounded-full border border-cream/25 text-cream transition-colors hover:border-gold disabled:opacity-30"
                          >
                            <span className="text-sm">−</span>
                          </button>
                          <span className="w-24 text-center text-xs font-semibold text-cream" data-testid="build-own-guests-count">
                            +{clusters * 25} guests
                          </span>
                          <button
                            data-testid="build-own-guests-plus"
                            onClick={() => setGuestClusters(clusters + 1)}
                            disabled={clusters >= 8}
                            aria-label="Add 25 guests"
                            className="flex h-9 w-9 items-center justify-center rounded-full border border-cream/25 text-cream transition-colors hover:border-gold disabled:opacity-30"
                          >
                            <span className="text-sm">+</span>
                          </button>
                        </div>
                      </div>

                      <div
                        data-testid="build-own-hours-row"
                        className={`flex flex-col items-start justify-between gap-3 rounded-2xl border p-4 transition-all duration-300 ${
                          hours > 0 ? "border-gold bg-white/[0.06]" : "border-cream/15 bg-white/[0.02]"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors duration-300 ${
                              hours > 0 ? "bg-gold text-espresso" : "bg-cream/10 text-gold"
                            }`}
                          >
                            <Clock className="h-4 w-4" />
                          </span>
                          <p className="text-sm text-cream/70">Extra party time — past the 5-hour mark</p>
                        </div>
                        <div className="flex items-center gap-3">
                          {hours > 0 && (
                            <span className="font-display text-xl text-gold" data-testid="build-own-hours-price">
                              +${hoursTotal.toLocaleString()}
                            </span>
                          )}
                          <button
                            data-testid="build-own-hours-minus"
                            onClick={() => setHours(hours - 1)}
                            disabled={hours === 0}
                            aria-label="Remove an hour"
                            className="flex h-9 w-9 items-center justify-center rounded-full border border-cream/25 text-cream transition-colors hover:border-gold disabled:opacity-30"
                          >
                            <span className="text-sm">−</span>
                          </button>
                          <span className="w-24 text-center text-xs font-semibold text-cream" data-testid="build-own-hours-count">
                            {hours} {hours === 1 ? "hour" : "hours"}
                          </span>
                          <button
                            data-testid="build-own-hours-plus"
                            onClick={() => setHours(hours + 1)}
                            disabled={hours >= 6}
                            aria-label="Add an hour"
                            className="flex h-9 w-9 items-center justify-center rounded-full border border-cream/25 text-cream transition-colors hover:border-gold disabled:opacity-30"
                          >
                            <span className="text-sm">+</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="locked"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    data-testid="build-own-locked"
                    className="mt-6 flex items-center gap-3 rounded-2xl border border-dashed border-cream/20 bg-white/[0.02] px-5 py-4"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-cream/10 text-gold">
                      <Lock className="h-4 w-4" />
                    </span>
                    <p className="text-sm text-cream/60">
                      Pick a package above — then add your extras, guests and extra time here.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="mt-8 flex flex-col items-center justify-between gap-3 rounded-2xl bg-white/[0.04] px-5 py-4 sm:flex-row">
                {buildPkg ? (
                  <p className="text-center text-xs text-cream/60 sm:text-left" data-testid="build-own-breakdown">
                    {buildPkg.name} ${buildPkg.price.toLocaleString()}
                    {addonsTotal > 0 &&
                      ` + ${build.addons.length} add-on${build.addons.length > 1 ? "s" : ""} $${addonsTotal.toLocaleString()}`}
                    {guestsTotal > 0 && ` + ${clusters * 25} add'l guests $${guestsTotal.toLocaleString()}`}
                    {hoursTotal > 0 && ` + ${hours} extra hr${hours > 1 ? "s" : ""} $${hoursTotal.toLocaleString()}`}
                    {" "}· 40% deposit: ${(buildPkg.price * 0.4).toFixed(0)}
                  </p>
                ) : (
                  <p className="text-xs text-cream/50" data-testid="build-own-empty-hint">
                    Pick a package above to see your total.
                  </p>
                )}
                <p className="flex items-baseline gap-2">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-cream/50">Your build</span>
                  <span className="font-display text-3xl text-gold" data-testid="build-own-total">
                    ${buildTotal.toLocaleString()}
                  </span>
                </p>
              </div>

              {secondThreshold && (
                <p
                  className="mt-4 flex items-start gap-2 text-[11px] leading-relaxed tracking-wide text-cream/50"
                  data-testid="build-own-second-note"
                >
                  <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" strokeWidth={3} />
                  At {secondThreshold}+ total guests a second bartender joins automatically — we'll
                  confirm staffing in your booking.
                </p>
              )}

              <button
                data-testid="build-own-book-btn"
                onClick={() => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" })}
                className="mt-6 w-full rounded-full bg-gold px-8 py-4 text-sm font-semibold uppercase tracking-[0.18em] text-espresso transition-all duration-300 hover:scale-[1.02] hover:bg-cream sm:w-auto"
              >
                Book your date
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
